/**
 * Two-Factor Login Prompt Component (Story 1.7)
 * Shows after successful password login if user is in grace period.
 */
import { ShieldExclamationIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { Button } from "../atoms/Button";

interface TwoFactorLoginPromptProps {
  daysRemaining: number;
  onSetupNow: () => void;
  onRemindLater: () => void;
}

export function TwoFactorLoginPrompt({
  daysRemaining,
  onSetupNow,
  onRemindLater,
}: TwoFactorLoginPromptProps) {
  const { t } = useTranslation("auth");
  const isUrgent = daysRemaining <= 2;

  return (
    <div className="w-full max-w-sm mx-auto text-center">
      <div
        className={`
          w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4
          ${
            isUrgent
              ? "bg-warning-100 dark:bg-warning-900/30"
              : "bg-primary-100 dark:bg-primary-900/30"
          }
        `}
      >
        <ShieldExclamationIcon
          className={`
            w-8 h-8
            ${isUrgent ? "text-warning-600 dark:text-warning-400" : "text-primary-600 dark:text-primary-400"}
          `}
        />
      </div>

      <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
        {t("twofa_setup_title")}
      </h2>

      <p className="text-neutral-600 dark:text-neutral-400 mb-2">
        {t("twofa_setup_description")}
      </p>

      <p
        className={`
          text-sm font-medium mb-6
          ${isUrgent ? "text-warning-600 dark:text-warning-400" : "text-primary-600 dark:text-primary-400"}
        `}
      >
        {t("twofa_setup_grace_days", { count: daysRemaining })}
      </p>

      <div className="space-y-3">
        <Button variant="primary" onClick={onSetupNow} className="w-full">
          {t("twofa_setup_now")}
        </Button>
        <Button variant="ghost" onClick={onRemindLater} className="w-full">
          {t("twofa_remind_later")}
        </Button>
      </div>

      <p className="mt-4 text-xs text-neutral-500 dark:text-neutral-500">
        {t("twofa_grace_expired_warning")}
      </p>
    </div>
  );
}
